// src/components/MayoristaInboxCharko.jsx
import { useEffect, useMemo, useState } from "react";
import {
  db,
  collection,
  query,
  onSnapshot,
  orderBy,
  where,
  limit,
  doc,
  updateDoc,
} from "../firebase";
import { toast } from "react-toastify";
import WhatsAppCloudChatWindow from "./WhatsAppCloudChatWindow";

const COMPANY_ID = "Charko";
const PHONE_NUMBER_ID = "928204713712286";

const CONVERSATIONS_PATH = `companies/${COMPANY_ID}/whatsapp_numbers/${PHONE_NUMBER_ID}/conversations`;
const ORDERS_COLLECTION = "orders";

const fmtDateTime = (ms) => {
  if (!ms) return "--";
  try {
    return new Date(ms).toLocaleString("es-AR", {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return "--";
  }
};

const fmtMoney = (n) =>
  new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS" }).format(
    Number(n || 0)
  );

function statusBadge(statusRaw) {
  const s = String(statusRaw || "").toLowerCase();
  if (s === "pending" || s === "pendiente") return { label: "Pendiente", cls: "badge-warning" };
  if (s === "aceptado" || s === "accepted") return { label: "Aceptado", cls: "badge-success" };
  if (s === "rechazado" || s === "rejected") return { label: "Rechazado", cls: "badge-error" };
  return { label: statusRaw || "--", cls: "badge-ghost" };
}

function getOrderPhone(o) {
  return (
    o?.cellPhone ||
    o?.rawWebhook?.customer?.billing_info?.buyerPhone ||
    o?.customer?.buyerPhone ||
    o?.phone ||
    ""
  );
}
function getOrderEmail(o) {
  return o?.customer?.email || o?.email || "";
}
function getOrderBusinessName(o) {
  return (
    o?.customer?.businessName ||
    o?.rawWebhook?.customer?.billing_info?.businessName ||
    ""
  );
}
function getOrderBuyerName(o) {
  return (
    o?.rawWebhook?.customer?.billing_info?.buyerName ||
    o?.customer?.fullName ||
    o?.rawWebhook?.customer?.full_name ||
    ""
  );
}
function getOrderTotal(o) {
  const direct =
    o?.total ||
    o?.totalAmount ||
    o?.amount ||
    o?.rawWebhook?.total_price ||
    o?.rawWebhook?.total ||
    0;

  if (Number(direct) > 0) return Number(direct);

  const items = Array.isArray(o?.rawWebhook?.items) ? o.rawWebhook.items : [];
  if (!items.length) return 0;

  return items.reduce((acc, it) => {
    const line =
      Number(it?.total_price || 0) ||
      Number(it?.unit_price || 0) * Number(it?.quantity || 0) ||
      0;
    return acc + line;
  }, 0);
}
function getOrderSummary(o) {
  const items = Array.isArray(o?.rawWebhook?.items) ? o.rawWebhook.items : [];
  if (!items.length) return "--";
  const distinct = items.length;
  const units = items.reduce((acc, it) => acc + Number(it?.quantity || 0), 0);
  return `${distinct} productos · ${units} unidades`;
}

export default function MayoristaInboxCharko() {
  // conversaciones
  const [convs, setConvs] = useState([]);
  const [selectedWaId, setSelectedWaId] = useState(null);
  const [convSearch, setConvSearch] = useState("");

  // pedidos
  const [orders, setOrders] = useState([]);
  const [orderSearch, setOrderSearch] = useState("");
  const [onlyPending, setOnlyPending] = useState(true);
  const [filterCompanyId, setFilterCompanyId] = useState(true);
  const [recentHours, setRecentHours] = useState(72);

  // UI
  const [showChatModal, setShowChatModal] = useState(false);

  // ✅ Suscripción conversaciones (usa lastMessageAt para excluir doc "conversations" meta)
  useEffect(() => {
    const now = Date.now();
    const cutoff = now - 30 * 24 * 60 * 60 * 1000;

    const q = query(
      collection(db, CONVERSATIONS_PATH),
      where("lastMessageAt", ">=", cutoff),
      orderBy("lastMessageAt", "desc"),
      limit(200)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setConvs(data);
        if (!selectedWaId && data.length) setSelectedWaId(data[0].wa_id || data[0].id);
      },
      (err) => {
        console.error(err);
        toast.error("Error al suscribirse a conversaciones mayorista");
      }
    );

    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedConv = useMemo(() => {
    if (!selectedWaId) return null;
    return convs.find((c) => (c.wa_id || c.id) === selectedWaId) || null;
  }, [convs, selectedWaId]);

  const filteredConvs = useMemo(() => {
    const s = convSearch.trim().toLowerCase();
    if (!s) return convs;

    return convs.filter((c) => {
      const wa = String(c?.wa_id || c?.id || "").toLowerCase();
      const name = String(c?.name || "").toLowerCase();
      const preview = String(c?.lastMessagePreview || "").toLowerCase();
      const comment = String(c?.comentario || "").toLowerCase();
      return wa.includes(s) || name.includes(s) || preview.includes(s) || comment.includes(s);
    });
  }, [convs, convSearch]);

  // ✅ Suscripción pedidos (cards derecha)
  useEffect(() => {
    const now = Date.now();
    const cutoff = now - Number(recentHours || 0) * 60 * 60 * 1000;

    const constraints = [
      where("createdAt", ">=", cutoff),
      orderBy("createdAt", "desc"),
      limit(200),
    ];

    if (onlyPending) constraints.unshift(where("status", "in", ["pending", "Pendiente"]));
    if (filterCompanyId) constraints.unshift(where("companyId", "==", "charko"));

    const q = query(collection(db, ORDERS_COLLECTION), ...constraints);

    const unsub = onSnapshot(
      q,
      (snap) => {
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setOrders(data);
      },
      (err) => {
        console.error(err);
        toast.error("Error al suscribirse a pedidos");
      }
    );

    return () => unsub();
  }, [onlyPending, filterCompanyId, recentHours]);

  const filteredOrders = useMemo(() => {
    const s = orderSearch.trim().toLowerCase();
    if (!s) return orders;
    return orders.filter((o) => {
      const email = getOrderEmail(o).toLowerCase();
      const phone = String(getOrderPhone(o)).toLowerCase();
      const num = String(o?.orderNumber || "").toLowerCase();
      const st = String(o?.status || "").toLowerCase();
      const buyer = getOrderBuyerName(o).toLowerCase();
      const business = getOrderBusinessName(o).toLowerCase();
      return (
        email.includes(s) ||
        phone.includes(s) ||
        num.includes(s) ||
        st.includes(s) ||
        buyer.includes(s) ||
        business.includes(s)
      );
    });
  }, [orders, orderSearch]);

  // ✅ marcar needAttention=false (opcional)
  const clearNeedAttention = async (waId) => {
    try {
      const ref = doc(db, CONVERSATIONS_PATH, waId);
      await updateDoc(ref, { needAttention: false, updatedAt: Date.now() });
    } catch (e) {
      console.error(e);
      toast.error("No se pudo actualizar needAttention");
    }
  };

  return (
    <div className="p-6">
      <div className="mb-4">
        <h1 className="text-3xl font-bold">Charko Mayorista — Inbox</h1>
        <p className="text-sm opacity-70">
          <code>{CONVERSATIONS_PATH}</code>
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* IZQUIERDA: cards conversaciones */}
        <div className="lg:col-span-7">
          <div className="bg-base-100 rounded-xl shadow border">
            <div className="p-4 border-b flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
              <div className="flex items-center gap-2">
                <div className="text-lg font-bold">Conversaciones (Mayorista)</div>
                <span className="badge badge-outline">{filteredConvs.length}</span>
              </div>

              <div className="flex items-center gap-2">
                <input
                  className="input input-bordered input-sm w-full sm:w-72"
                  placeholder="Buscar (wa_id, nombre, preview)"
                  value={convSearch}
                  onChange={(e) => setConvSearch(e.target.value)}
                />
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => {
                    if (!selectedConv) return toast.info("No hay conversación seleccionada");
                    setShowChatModal(true);
                  }}
                >
                  Abrir chat
                </button>
              </div>
            </div>

            <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {filteredConvs.length ? (
                filteredConvs.map((c) => {
                  const waId = c?.wa_id || c?.id;
                  const active = waId === selectedWaId;

                  return (
                    <button
                      key={waId}
                      className={`text-left p-4 rounded-xl border shadow-sm hover:shadow transition ${
                        active ? "border-primary bg-primary/5" : "bg-base-100"
                      }`}
                      onClick={() => setSelectedWaId(waId)}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-bold truncate">
                            {c?.name || "(sin nombre)"}
                          </div>
                          <div className="text-xs opacity-70 truncate">
                            <code>{waId}</code>
                          </div>
                        </div>
                        <div className="text-xs opacity-70 whitespace-nowrap">
                          {fmtDateTime(c?.lastMessageAt || c?.updatedAt || c?.createdAt)}
                        </div>
                      </div>

                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        {c?.needAttention ? (
                          <span className="badge badge-warning">Necesita atención</span>
                        ) : (
                          <span className="badge badge-ghost">OK</span>
                        )}
                        <span className="badge badge-outline">
                          {c?.channel || "whatsapp_cloud"}
                        </span>
                      </div>

                      {!!c?.lastMessagePreview && (
                        <div className="mt-3 text-sm opacity-80 line-clamp-2">
                          {c.lastMessagePreview}
                        </div>
                      )}

                      {c?.needAttention && (
                        <div className="mt-3">
                          <button
                            type="button"
                            className="btn btn-xs btn-outline"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              clearNeedAttention(waId);
                            }}
                          >
                            Marcar como visto
                          </button>
                        </div>
                      )}
                    </button>
                  );
                })
              ) : (
                <div className="text-sm opacity-70">No hay conversaciones para mostrar.</div>
              )}
            </div>
          </div>
        </div>

        {/* DERECHA: pedidos en cards */}
        <div className="lg:col-span-5">
          <div className="bg-base-100 rounded-xl shadow border">
            <div className="p-4 border-b flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <div className="text-lg font-bold">Pedidos nuevos</div>
                <span className="badge badge-outline">{filteredOrders.length}</span>
              </div>

              <input
                className="input input-bordered input-sm w-full"
                placeholder="Buscar pedido (email, teléfono, #, estado, razón social)"
                value={orderSearch}
                onChange={(e) => setOrderSearch(e.target.value)}
              />

              <div className="flex flex-wrap items-center gap-2">
                <label className="cursor-pointer flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="checkbox checkbox-sm"
                    checked={onlyPending}
                    onChange={(e) => setOnlyPending(e.target.checked)}
                  />
                  <span className="text-sm">Solo pendientes</span>
                </label>

                <label className="cursor-pointer flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="checkbox checkbox-sm"
                    checked={filterCompanyId}
                    onChange={(e) => setFilterCompanyId(e.target.checked)}
                  />
                  <span className="text-sm">companyId = charko</span>
                </label>

                <div className="flex items-center gap-2">
                  <span className="text-sm opacity-70">Últimas</span>
                  <select
                    className="select select-bordered select-sm"
                    value={recentHours}
                    onChange={(e) => setRecentHours(Number(e.target.value))}
                  >
                    <option value={24}>24h</option>
                    <option value={48}>48h</option>
                    <option value={72}>72h</option>
                    <option value={168}>7 días</option>
                  </select>
                </div>
              </div>

              <div className="alert alert-info py-2">
                <span className="text-xs">
                  Si tu <code>orders</code> no tiene <code>companyId</code> o <code>status</code> así, desmarcá los checks.
                </span>
              </div>
            </div>

            <div className="p-4 flex flex-col gap-3">
              {filteredOrders.length ? (
                filteredOrders.map((o) => {
                  const badge = statusBadge(o?.status);
                  const num = o?.orderNumber ?? "--";
                  const createdAt = o?.createdAt || null;
                  const email = getOrderEmail(o);
                  const phone = getOrderPhone(o);
                  const buyer = getOrderBuyerName(o);
                  const business = getOrderBusinessName(o);
                  const total = getOrderTotal(o);
                  const summary = getOrderSummary(o);

                  return (
                    <div
                      key={o.id}
                      className="text-left p-4 rounded-xl border shadow-sm bg-base-100"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-bold">
                          Pedido <span className="opacity-80">#{num}</span>
                        </div>
                        <div className="text-xs opacity-70 whitespace-nowrap">
                          {fmtDateTime(createdAt)}
                        </div>
                      </div>

                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <span className={`badge ${badge.cls}`}>{badge.label}</span>
                        <span className="badge badge-outline">{summary}</span>
                        {total > 0 && <span className="badge badge-ghost">{fmtMoney(total)}</span>}
                      </div>

                      <div className="mt-3 text-sm space-y-1">
                        {!!email && (
                          <div className="opacity-80 truncate">
                            <strong>Email:</strong> {email}
                          </div>
                        )}
                        {!!buyer && (
                          <div className="opacity-80 truncate">
                            <strong>Comprador:</strong> {buyer}
                          </div>
                        )}
                        {!!business && (
                          <div className="opacity-80 truncate">
                            <strong>Razón social:</strong> {business}
                          </div>
                        )}
                        {!!phone && (
                          <div className="opacity-80 truncate">
                            <strong>Tel:</strong> <code>{phone}</code>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-sm opacity-70">No hay pedidos para mostrar.</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* MODAL Chat */}
      {showChatModal && selectedConv && (
        <div className="modal modal-open">
          <div className="modal-box max-w-6xl p-0 overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between">
              <div className="font-bold">
                Chat Mayorista — {selectedConv?.name || selectedWaId}
                <span className="ml-2 text-xs opacity-70">
                  <code>{selectedWaId}</code>
                </span>
              </div>
              <button className="btn btn-sm" onClick={() => setShowChatModal(false)}>
                Cerrar
              </button>
            </div>

            <div className="h-[78vh]">
              <WhatsAppCloudChatWindow
                companyId={COMPANY_ID}
                phoneNumberId={PHONE_NUMBER_ID}
                waId={selectedWaId}
              />
            </div>
          </div>
          <div className="modal-backdrop" onClick={() => setShowChatModal(false)} />
        </div>
      )}
    </div>
  );
}
